import React, { useState, useEffect } from 'react';
import { submitKYC, fileToBase64, checkKycBuyerId } from '../services/api';
import TermsAndConditions from './TermsAndConditions';
import './KYCPage.css';

const KYCPage = () => {
    const [buyerId, setBuyerId] = useState('');
    const [fullName, setFullName] = useState('');
    const [idType, setIdType] = useState('Aadhar Card');
    const [idNumber, setIdNumber] = useState('');
    const [documentFile, setDocumentFile] = useState(null);
    const [tncAccepted, setTncAccepted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const [error, setError] = useState('');
    const [duplicateError, setDuplicateError] = useState('');
    const [isCheckingId, setIsCheckingId] = useState(false);


    // Debounce check for details
    useEffect(() => {
        const checkBuyerId = async () => {
            if (!buyerId.trim()) {
                setDuplicateError('');
                return;
            }

            setIsCheckingId(true);
            try {
                const response = await checkKycBuyerId(buyerId.trim());
                if (response.success) {
                    if (!response.data.exists) {
                        setDuplicateError('Buyer ID not registered.');
                    } else if (['Submitted', 'Verified', 'Accepted'].includes(response.data.kycStatus)) {
                        setDuplicateError(`KYC already ${response.data.kycStatus}.`);
                    } else {
                        setDuplicateError('');
                    }
                }
            } catch (err) {
                console.error('Error checking Buyer ID:', err);
            } finally {
                setIsCheckingId(false);
            }
        };

        const timeoutId = setTimeout(() => {
            if (buyerId) checkBuyerId();
            else setDuplicateError('');
        }, 800);

        return () => clearTimeout(timeoutId);
    }, [buyerId]);

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setDocumentFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        if (!tncAccepted) {
            setError('You must accept the Terms & Conditions to proceed.');
            return;
        }

        if (!buyerId || !fullName || !idNumber || !documentFile) {
            setError('Please fill in all required fields and upload your ID document.');
            return;
        }

        if (duplicateError) {
            setError('Please fix the errors before submitting.');
            return;
        }

        setLoading(true);

        try {
            const base64File = await fileToBase64(documentFile);

            const kycData = {
                buyerId: buyerId.trim(),
                fullName: fullName.trim(),
                idType,
                idNumber: idNumber.trim(),
                document: base64File
            };

            const response = await submitKYC(kycData);

            if (response.success) {
                setSuccess(true);
                setBuyerId('');
                setFullName('');
                setIdNumber('');
                setDocumentFile(null);
                setTncAccepted(false);
            } else {
                setError(response.message || 'KYC submission failed. Please try again.');
            }
        } catch (err) {
            console.error('KYC Submit Error:', err);
            setError('An error occurred. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="kyc-page">
                <div className="kyc-container">
                    <div className="kyc-success-card">
                        <div className="success-icon"><i className="ri-shield-check-line"></i></div>
                        <h2>Verification Submitted!</h2>
                        <p>Your KYC documents and acceptance of Terms & Conditions have been successfully submitted.</p>
                        <p>Our team will review your details and activate your account shortly.</p>
                        <div className="kyc-admin-note">
                            <i className="ri-information-line"></i> You may close this page now.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="kyc-page">
            <header className="kyc-header">
                <h1>Collaboration World</h1>
                <p>Identity Verification & Agreement</p>
            </header>



            <div className="kyc-container">
                <form onSubmit={handleSubmit} className="kyc-form">
                    {/* Buyer ID Section */}
                    <section className="kyc-section">
                        <h3><i className="ri-user-search-line"></i> User Identification</h3>
                        <p className="section-desc">Please enter your registered Buyer ID.</p>
                        <div className="form-group">
                            <label htmlFor="buyerId">Buyer ID <span className="required">*</span></label>
                            <input
                                type="text"
                                id="buyerId"
                                value={buyerId}
                                onChange={(e) => setBuyerId(e.target.value.toUpperCase())}
                                placeholder="e.g., CW00001"
                                disabled={loading}
                                required
                                className={duplicateError ? 'input-error' : ''}
                            />
                            {isCheckingId && <small className="form-hint" style={{ color: '#1e3a8a' }}>Checking Buyer ID...</small>}
                            {duplicateError && <small className="form-hint" style={{ color: '#b91c1c', fontWeight: 'bold' }}>{duplicateError}</small>}
                        </div>
                    </section>

                    {/* T&C Section */}
                    <section className="kyc-section">
                        <h3><i className="ri-file-text-line"></i> Terms & Conditions</h3>
                        <p className="section-desc">Please read carefully before proceeding.</p>

                        <div className="tnc-scroll-box">
                            <TermsAndConditions isEmbedded={true} />
                        </div>

                        <div className="tnc-checkbox-wrapper">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={tncAccepted}
                                    onChange={(e) => setTncAccepted(e.target.checked)}
                                    disabled={loading}
                                />
                                <span className="checkbox-text">
                                    I have read, understood, and agree to all the Terms & Conditions.
                                </span>
                            </label>
                        </div>
                    </section>

                    {/* Verification Details */}
                    <section className="kyc-section">
                        <h3><i className="ri-id-card-line"></i> Identity Verification</h3>
                        <div className="form-grid">
                            <div className="form-group">
                                <label htmlFor="fullName">Full Name <span className="required">*</span></label>
                                <input
                                    type="text"
                                    id="fullName"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    placeholder="Your Name"
                                    disabled={loading}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="idType">ID Type <span className="required">*</span></label>
                                <select
                                    id="idType"
                                    value={idType}
                                    onChange={(e) => setIdType(e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="Aadhar Card">Aadhar Card</option>
                                    <option value="PAN Card">PAN Card</option>
                                    <option value="Voter ID">Voter ID</option>
                                    <option value="Passport">Passport</option>
                                    <option value="Driving License">Driving License</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="idNumber">ID Number <span className="required">*</span></label>
                                <input
                                    type="text"
                                    id="idNumber"
                                    value={idNumber}
                                    onChange={(e) => setIdNumber(e.target.value)}
                                    placeholder="Enter your ID Number"
                                    disabled={loading}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="document">Upload Document <span className="required">*</span></label>
                                <input
                                    type="file"
                                    id="document"
                                    accept="image/*,application/pdf"
                                    onChange={handleFileChange}
                                    disabled={loading}
                                    required
                                />
                                <small className="form-hint">Clear image or PDF of your selected ID.</small>
                            </div>
                        </div>
                    </section>

                    {error && <div className="kyc-error">{error}</div>}

                    <button type="submit" className="btn-submit-kyc" disabled={loading}>
                        {loading ? 'Submitting...' : 'Submit Verification'}
                    </button>
                </form>
            </div>

            <footer className="kyc-footer">
                <p>&copy; 2026 Collaboration World. All rights reserved.</p>
            </footer>
        </div >
    );
};

export default KYCPage;
